import type { DocsService } from '@affine/core/modules/doc';
import type { GuardService } from '@affine/core/modules/permissions';
import { Service } from '@toeverything/infra';
import { combineLatest, map, type Observable, of, switchMap } from 'rxjs';

import type { FilterProvider } from '../../provider';
import type { FilterParams } from '../../types';

/**
 * Per-doc access filter (the UI half of the internal-team access model).
 *
 * The 0.27.0 server already refuses to sync a doc's content to a member who
 * lacks `Doc.Read` (see the server-side `onLoadSpaceDoc` gate + the
 * default-role→None patch). This filter is the matching UI layer: it keeps docs
 * the current user can't read OUT of the doc lists, so a member sees only the
 * maps they created or were explicitly granted — not just an un-openable title.
 *
 * Resolves to the set of docs the current user can `Doc_Read`:
 *  - Workspace owner/admin short-circuit to "all" inside GuardService
 *    (`revalidateCan` returns early for admins — no per-doc queries).
 *  - A plain member gets one permission check per doc; results are cached and
 *    revalidated by GuardService. A doc is included only once its check has
 *    resolved to `true` (fail-closed: hidden while loading, never flashed).
 */
export class AccessibleFilterProvider extends Service implements FilterProvider {
  constructor(
    private readonly docsService: DocsService,
    private readonly guardService: GuardService
  ) {
    super();
  }

  filter$(_params: FilterParams): Observable<Set<string>> {
    return this.docsService.allNonTrashDocIds$().pipe(
      switchMap(ids => {
        if (ids.length === 0) {
          return of(new Set<string>());
        }
        return combineLatest(
          ids.map(id => {
            // trigger the fetch (deduped per-doc inside GuardService), then
            // observe the reactive result
            this.guardService.revalidateCan('Doc_Read', id);
            return this.guardService
              .can$('Doc_Read', id)
              .pipe(map(can => [id, can === true] as const));
          })
        ).pipe(
          map(
            pairs =>
              new Set(pairs.filter(([, can]) => can).map(([id]) => id))
          )
        );
      })
    );
  }
}
